import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColDef, GridRenderCellParams, GridRowClassNameParams } from '@mui/x-data-grid';

import MultiElementSelector from './MultiElementSelector';
import { provideTargetIcon } from '../IconProvider';
import {
  isTargetInactiveByState,
  TargetNameAndDescription,
  targetNameAndDescriptionStyle,
} from '../TargetNameAndDescription';
import { dbToArray, getVehicleCountByFilter, searchTargetsByNameFragment } from '@/components/db';
import { CountResponseDto, MinimalTarget } from '@/generated/model';
import { norm } from '@/components/db/initDb';

export default function MultiTargetSelector({
  selectedTargets,
  setSelectedTargets,
  searchText,
  setSearchText,
  maxElementNumber,
  columnsOverride,
  rowClassProvider,
}: {
  selectedTargets: MinimalTarget[];
  setSelectedTargets: Dispatch<SetStateAction<MinimalTarget[]>>;
  searchText: string | undefined;
  setSearchText: Dispatch<SetStateAction<string | undefined>>;
  maxElementNumber: number;
  columnsOverride?: GridColDef[];
  rowClassProvider?: (params: GridRowClassNameParams<MinimalTarget>) => string;
}) {
  async function fetchData(
    page: number,
    pageSize: number,
    pagesToLoad: number,
    filter: string | undefined,
    order: string
  ): Promise<MinimalTarget[]> {
    const offset = page * pageSize;
    const limit = pageSize * pagesToLoad;

    try {
      let results: MinimalTarget[];

      if (filter) {
        results = await searchTargetsByNameFragment(filter, offset, limit, order);
      } else {
        results = await dbToArray(offset, limit, order);
      }

      return results.map((row) => ({
        id: row.id,
        name: row.name,
        description: row.description,
        category: row.category,
        state: row.state,
        comment: row.comment,
        customerName: row.customerName,
      }));
    } catch (error) {
      console.error('Local fetch failed:', error);
      return [];
    }
  }
  const { t } = useTranslation();

  async function getRowCount(filter: string | undefined, ids: number[]): Promise<CountResponseDto> {
    try {
      return await getVehicleCountByFilter(filter, ids);
    } catch (error) {
      console.error('Count fetch failed:', error);
      // TODO userfriendly error handling
      return { count: 0, ids: [] };
    }
  }

  let columns: GridColDef[];
  if (columnsOverride) {
    columns = columnsOverride;
  } else {
    columns = [
      {
        field: 'category',
        headerName: 'Category',
        flex: 0.1,
        renderCell: (params: GridRenderCellParams) => {
          const target = params.row;
          return (
            <div className="target-cell" data-qa-element-id="target_icon">
              <img className="idata-target-icon" src={provideTargetIcon(target.category)} />
            </div>
          );
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => {
          const isChecked = selectedTargets
            ? selectedTargets.find((target) => target.id === params.row.id) != undefined
            : false;
          const target = params.row;
          return (
            <div className="target-cell">
              <TargetNameAndDescription
                targetName={target.name}
                targetDescription={target.description}
                styleClassName={targetNameAndDescriptionStyle(isChecked, true, isTargetInactiveByState(target))}
              />
            </div>
          );
        },
      },
    ];
  }

  const translation = {
    header: t('general.vehicleSelector'),
    placeHolder: t('filter.targetSearchPlaceholder'),
    noRows: t('filter.noRows'),
    tooManySelected: t('error.api.too_many_target_selected', { limit: maxElementNumber }),
  };

  function searchFilter(target: MinimalTarget, localSearchText: string) {
    return (
      (!!target.name && norm(target.name).includes(norm(localSearchText))) ||
      (!!target.description && norm(target.description).includes(norm(localSearchText))) ||
      (!!target.customerName && norm(target.customerName).includes(norm(localSearchText))) ||
      (!!target.comment && norm(target.comment).includes(norm(localSearchText)))
    );
  }

  return (
    <MultiElementSelector
      selectedElements={selectedTargets}
      setSelectedElements={setSelectedTargets}
      searchText={searchText}
      setSearchText={setSearchText}
      maxElementNumber={maxElementNumber}
      fetchData={fetchData}
      getRowCount={getRowCount}
      columns={columns}
      translation={translation}
      rowClassProvider={rowClassProvider}
      searchFilter={searchFilter}
    />
  );
}
